import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Calendar, Clock, User, MapPin, FileText, Plus, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Doctor, Staff, Room, ScheduleItem, Appointment } from './SchedulerTypes';
import { useClinic } from '@/contexts/ClinicContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { CalendarIcon } from "@radix-ui/react-icons"
import { format } from "date-fns"
import { formatDateToString } from '../lib/dateUtils';
import { getLeaveSeries } from '../lib/scheduleSeries';
import { useQueryClient } from '@tanstack/react-query';
import {
  updateScheduleItem as updateScheduleItemRow,
  deleteScheduleItems as deleteScheduleItemRows,
} from '@/services/adminService';
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import ScheduleFormData from './ScheduleFormData';
import ConflictResolutionModal, { AffectedSchedule, Reassignment } from './ConflictResolutionModal';
import { detectAffectedSchedules } from '@/services/conflictDetectionService';
import { getDoctorPreferences } from '@/services/adminService';

interface ScheduleData {
  title: string;
  type: 'appointment' | 'admin' | 'break' | 'leave' | 'mc';
  doctorId?: string;
  staffId?: string;
  roomId?: string;
  startTime: string;
  endTime: string;
  notes?: string;
  date: string;
  endDate?: string;
  clinic_id?: string;
}

interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (scheduleData: ScheduleData, reassignments?: Reassignment[]) => void;
  onDelete?: (scheduleId: string) => void;
  onDeleteSeries?: (ids: string[]) => void;
  onCreateAppointment: (appointmentData: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => void;
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  initialData: ScheduleItem | null;
  defaultStartTime: string;
  currentDate: Date;
  selectedPersonId?: string;
  selectedIsDoctor?: boolean;
  scheduleItems?: ScheduleItem[];
}

// Helper function to convert time format from HH:MM:SS to HH:MM
const formatTime = (time: string): string => {
  if (!time) return '';
  // If time already in HH:MM format, return as is
  if (time.length === 5 && time.includes(':')) return time;
  // If time is in HH:MM:SS format, remove the seconds part
  if (time.length === 8 && time.includes(':')) {
    return time.substring(0, 5);
  }
  return time;
};

const ScheduleModal = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  onDeleteSeries,
  onCreateAppointment,
  doctors,
  staff,
  rooms,
  initialData,
  defaultStartTime,
  currentDate,
  selectedPersonId,
  selectedIsDoctor,
  scheduleItems = [],
}: ScheduleModalProps) => {
  const { toast } = useToast();
  const { currentClinic } = useClinic();
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'appointment' | 'admin' | 'break' | 'leave' | 'mc'>('appointment');
  const [selectedDoctor, setSelectedDoctor] = useState<string | undefined>(undefined);
  const [selectedStaff, setSelectedStaff] = useState<string | undefined>(undefined);
  const [selectedRoom, setSelectedRoom] = useState<string | undefined>(undefined);
  const [startTime, setStartTime] = useState(defaultStartTime || '09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(currentDate);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingSeries, setIsSavingSeries] = useState(false);
  const queryClient = useQueryClient();
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [affectedSchedules, setAffectedSchedules] = useState<AffectedSchedule[]>([]);
  const [pendingReassignments, setPendingReassignments] = useState<Reassignment[]>([]);
  const [orderedStaff, setOrderedStaff] = useState<Staff[]>(staff);

  // Track which doctor we've auto-assigned preferences for to prevent duplicate assignments
  const hasAutoAssigned = useRef<string | null>(null);

  // Reset auto-assignment tracking when modal closes or doctor is cleared
  useEffect(() => {
    if (!isOpen || !selectedDoctor) {
      hasAutoAssigned.current = null;
    }
  }, [isOpen, selectedDoctor]);

  useEffect(() => {
    if (initialData) {
      // Editing existing item - load all existing values
      setTitle(initialData.title);
      setType(initialData.type);
      setSelectedDoctor(initialData.doctorId);
      setSelectedStaff(initialData.staffId);
      setSelectedRoom(initialData.roomId);
      
      // Convert time format from HH:MM:SS to HH:MM for dropdown compatibility
      const formattedStartTime = formatTime(initialData.startTime);
      const formattedEndTime = formatTime(initialData.endTime);
      
      setStartTime(formattedStartTime);
      setEndTime(formattedEndTime);
      setNotes(initialData.notes || '');
      setDate(new Date(initialData.date));
      setEndDate(initialData.endDate ? new Date(initialData.endDate) : undefined);
    } else {
      // Creating new item - reset to defaults
      setTitle('');
      setType('appointment');
      setSelectedRoom(undefined);
      setStartTime(defaultStartTime || '09:00');
      setEndTime('18:00');
      setNotes('');
      setDate(currentDate);
      setEndDate(undefined);
      
      // Pre-select person based on clicked timeslot
      if (selectedPersonId && selectedIsDoctor !== undefined) {
        if (selectedIsDoctor) {
          setSelectedDoctor(selectedPersonId);
          setSelectedStaff(undefined);
        } else {
          setSelectedStaff(selectedPersonId);
          setSelectedDoctor(undefined);
        }
      } else {
        setSelectedDoctor(undefined);
        setSelectedStaff(undefined);
      }
    }
  }, [initialData, isOpen]); // Removed defaultStartTime, currentDate, selectedPersonId, selectedIsDoctor to prevent overriding existing data

  // Handle new schedule creation parameters separately when modal opens
  useEffect(() => {
    // Only apply these when creating a new schedule (no initialData) and modal is opening
    if (!initialData && isOpen) {
      setStartTime(defaultStartTime || '09:00');
      setDate(currentDate);

      // Apply person selection for new schedules
      if (selectedPersonId && selectedIsDoctor !== undefined) {
        if (selectedIsDoctor) {
          setSelectedDoctor(selectedPersonId);
          setSelectedStaff(undefined);
        } else {
          setSelectedStaff(selectedPersonId);
          setSelectedDoctor(undefined);
        }
      }
    }
  }, [defaultStartTime, currentDate, selectedPersonId, selectedIsDoctor, isOpen, initialData]);

  // Auto-assign Main Nurse and preferred room when doctor is selected (for new schedules only)
  // Also order staff dropdown by preference rank
  useEffect(() => {
    const autoAssignDoctorPreferences = async () => {
      // Reset to default staff order if no doctor selected
      if (!selectedDoctor) {
        setOrderedStaff(staff);
        return;
      }

      try {
        // Get doctor's preferences
        const preferences = await getDoctorPreferences(selectedDoctor);

        // Order staff by preference rank (1 → 2 → 3 → 4 → others)
        const newOrderedStaff: Staff[] = [];

        // Add staff in preference order (rank 1-4)
        for (let rank = 1; rank <= 4; rank++) {
          const pref = preferences.find(p => p.preference_rank === rank);
          if (pref) {
            const staffMember = staff.find(s => s.id === pref.staff_id);
            if (staffMember) {
              newOrderedStaff.push(staffMember);
            }
          }
        }

        // Add remaining staff (those not in preferences)
        const preferredIds = preferences.map(p => p.staff_id);
        const remainingStaff = staff.filter(s => !preferredIds.includes(s.id));
        newOrderedStaff.push(...remainingStaff);

        setOrderedStaff(newOrderedStaff);

        // Only auto-assign for new schedules (not editing)
        if (initialData) return;

        // Check if we've already auto-assigned for this doctor
        if (hasAutoAssigned.current === selectedDoctor) {
          console.log('[ScheduleModal] Auto-assignment already done for doctor:', selectedDoctor);
          return;
        }

        console.log('[ScheduleModal] Starting auto-assignment for doctor:', selectedDoctor);

        // Get doctor details for preferred room
        const doctor = doctors.find(d => d.id === selectedDoctor);

        // Find Main Nurse (rank 1)
        const mainNurse = preferences.find(p => p.preference_rank === 1);

        console.log('[ScheduleModal] Auto-assignment data:', {
          doctorId: selectedDoctor,
          doctorName: doctor?.name,
          mainNurseId: mainNurse?.staff_id,
          preferredRoomId: doctor?.preferred_room_id,
          currentStaff: selectedStaff,
          currentRoom: selectedRoom
        });

        // Mark as auto-assigned BEFORE making state changes
        hasAutoAssigned.current = selectedDoctor;

        // Auto-assign Main Nurse if available and no staff selected
        if (mainNurse && mainNurse.staff_id) {
          console.log('[ScheduleModal] Setting staff to:', mainNurse.staff_id);
          setSelectedStaff(mainNurse.staff_id);
        }

        // Auto-assign preferred room if available and no room selected
        if (doctor?.preferred_room_id) {
          console.log('[ScheduleModal] Setting room to:', doctor.preferred_room_id);
          setSelectedRoom(doctor.preferred_room_id);
        }

        console.log('[ScheduleModal] Auto-assignment completed');
      } catch (error) {
        console.error('[ScheduleModal] Error auto-assigning preferences:', error);
        // Don't show error to user - just skip auto-assignment
        setOrderedStaff(staff);
      }
    };

    autoAssignDoctorPreferences();
  }, [selectedDoctor, initialData, doctors, staff]);

  const handleCheckConflicts = () => {
    // Only check conflicts for leave/MC types with a staff member selected
    if ((type === 'leave' || type === 'mc') && selectedStaff) {
      const affected = detectAffectedSchedules(
        selectedStaff,
        date,
        endDate,
        scheduleItems,
        doctors,
        rooms
      );
      
      setAffectedSchedules(affected);
      if (affected.length > 0) {
        setShowConflictModal(true);
      } else {
        // No conflicts, proceed with save
        handleSave();
      }
    } else {
      handleSave();
    }
  };

  const handleConfirmReassignments = (reassignments: Reassignment[]) => {
    setPendingReassignments(reassignments);
    setShowConflictModal(false);
    // Now save with reassignments
    handleSaveWithReassignments(reassignments);
  };

  const handleSaveWithReassignments = (reassignments?: Reassignment[]) => {
    setIsSaving(true);

    const scheduleData = {
      title,
      type,
      doctorId: selectedDoctor,
      staffId: selectedStaff,
      roomId: selectedRoom,
      startTime,
      endTime,
      notes,
      date: formatDateToString(date),
      endDate: endDate ? formatDateToString(endDate) : undefined,
      clinic_id: currentClinic?.id || '',
    };
    
    console.log('[ScheduleModal] Saving with reassignments:', { scheduleData, reassignments });
    
    onSave(scheduleData, reassignments);
    onClose();
    
    // Reset states
    setTimeout(() => {
      setIsSaving(false);
      setPendingReassignments([]);
      setAffectedSchedules([]);
    }, 100);
  };

  const handleSave = () => {
    console.log('[ScheduleModal] handleSave called');
    console.log('[ScheduleModal] Form state:', { 
      title, 
      type, 
      selectedDoctor, 
      selectedStaff, 
      selectedRoom,
      startTime,
      endTime,
      date,
      endDate,
      currentClinic: currentClinic?.name 
    });
    
    // Validate that at least one person is assigned
    if (!selectedDoctor && !selectedStaff) {
      console.error('[ScheduleModal] No person selected');
      toast({
        title: "Validation Error",
        description: "Please select at least one person (doctor or staff) for this schedule.",
        variant: "destructive",
      });
      return;
    }

    // Validate that a clinic is selected
    if (!currentClinic) {
      console.error('[ScheduleModal] No clinic selected');
      toast({
        title: "No Clinic Selected",
        description: "Please select a clinic before creating a schedule. You cannot create schedules in the 'All Clinics' view.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    const scheduleData = {
      title,
      type,
      doctorId: selectedDoctor,
      staffId: selectedStaff,
      roomId: selectedRoom,
      startTime,
      endTime,
      notes,
      date: formatDateToString(date),
      endDate: endDate ? formatDateToString(endDate) : undefined,
      clinic_id: currentClinic.id,
    };
    
    console.log('[ScheduleModal] Calling onSave with data:', scheduleData);
    
    onSave(scheduleData, undefined);
    // Close the modal immediately, the loading overlay will show at the app level
    onClose();
    
    // Reset saving state after a short delay
    setTimeout(() => setIsSaving(false), 100);
  };

  // For a leave/MC item, work out the whole contiguous leave block so the user
  // can remove all its days in one action (leaves are stored as either a single
  // range row or many single-day rows - see lib/scheduleSeries).
  const leaveSeries = useMemo(() => {
    if (
      initialData &&
      (initialData.type === 'leave' || initialData.type === 'mc') &&
      scheduleItems &&
      scheduleItems.length > 0
    ) {
      return getLeaveSeries(initialData, scheduleItems);
    }
    return null;
  }, [initialData, scheduleItems]);

  const isMultiDayLeave = !!leaveSeries && leaveSeries.dayCount > 1;

  const formatLeaveDate = (dateStr: string): string => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return format(new Date(y, m - 1, d), 'd MMM yyyy');
  };

  const handleDelete = () => {
    if (initialData && onDelete) {
      onDelete(initialData.id);
      onClose();
    }
  };

  const handleDeleteEntireLeave = () => {
    if (leaveSeries && onDeleteSeries) {
      onDeleteSeries(leaveSeries.baseIds);
      onClose();
    } else {
      handleDelete();
    }
  };

  // Plato-synced leave must be changed in Plato — a local edit would be
  // overwritten (or fight) the next sync.
  const seriesHasPlatoRows = useMemo(() => {
    if (!leaveSeries || !scheduleItems) return false;
    const baseSet = new Set(leaveSeries.baseIds);
    return scheduleItems.some(
      (it) =>
        baseSet.has(it.id.split('_day_')[0]) &&
        (it as { external_source?: string }).external_source === 'plato'
    );
  }, [leaveSeries, scheduleItems]);

  /**
   * One-action amend/shorten of a whole multi-day leave (Khine, 9 Jul: no more
   * editing every date one by one). Rewrites the series as a SINGLE range row
   * with the form's dates/times, then removes the other rows of the old series.
   */
  const handleUpdateEntireLeave = async () => {
    if (!leaveSeries || !initialData || !date) return;
    const anchorBase = initialData.id.split('_day_')[0];
    const newStart = formatDateToString(date);
    const newEnd = endDate ? formatDateToString(endDate) : newStart;
    if (newEnd < newStart) {
      toast({ title: 'Check the dates', description: 'The end date is before the start date.', variant: 'destructive' });
      return;
    }
    setIsSavingSeries(true);
    try {
      await updateScheduleItemRow(anchorBase, {
        date: newStart,
        endDate: newEnd,
        startTime,
        endTime,
        type,
        title,
        notes,
        staffId: selectedStaff || undefined,
        doctorId: selectedDoctor || undefined,
      });
      const rest = leaveSeries.baseIds.filter((id) => id !== anchorBase);
      if (rest.length > 0) {
        await deleteScheduleItemRows(rest);
      }
      await queryClient.invalidateQueries({ queryKey: ['scheduleItems'] });
      toast({
        title: 'Leave updated',
        description: `Now runs ${formatLeaveDate(newStart)} to ${formatLeaveDate(newEnd)} as one entry.`,
      });
      onClose();
    } catch (error) {
      console.error('[ScheduleModal] Failed to update leave series:', error);
      toast({
        title: 'Could not update the leave',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingSeries(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{initialData ? 'Edit Schedule' : 'Add Schedule'}</DialogTitle>
          <DialogDescription>
            {initialData ? 'Edit the schedule details.' : 'Create a new schedule entry. Add an end date to create a recurring schedule across multiple days.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <ScheduleFormData
            title={title}
            setTitle={setTitle}
            type={type}
            setType={setType}
            selectedDoctor={selectedDoctor}
            setSelectedDoctor={setSelectedDoctor}
            doctors={doctors}
            selectedStaff={selectedStaff}
            setSelectedStaff={setSelectedStaff}
            staff={staff}
            orderedStaff={orderedStaff}
            selectedRoom={selectedRoom}
            setSelectedRoom={setSelectedRoom}
            rooms={rooms}
            startTime={startTime}
            setStartTime={setStartTime}
            endTime={endTime}
            setEndTime={setEndTime}
            notes={notes}
            setNotes={setNotes}
            date={date}
            setDate={setDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {isMultiDayLeave && initialData && (
            <Button
              type="button"
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={handleUpdateEntireLeave}
              disabled={isSavingSeries || seriesHasPlatoRows}
              title={seriesHasPlatoRows
                ? 'This leave is synced from Plato - change it in Plato and re-sync instead'
                : `Replaces all ${leaveSeries?.dayCount} days with the dates set above, in one action`}
            >
              {isSavingSeries ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Calendar className="mr-2 h-4 w-4" />
              )}
              Apply dates to entire leave ({leaveSeries?.dayCount} days)
            </Button>
          )}
          {initialData && onDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="destructive" className="w-full sm:w-auto">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Schedule
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {isMultiDayLeave ? 'Delete leave' : 'Delete Schedule'}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {isMultiDayLeave && leaveSeries ? (
                      <>
                        This leave runs {formatLeaveDate(leaveSeries.startDate)} to {formatLeaveDate(leaveSeries.endDate)} ({leaveSeries.dayCount} days).
                        You can remove the whole leave at once, or just this one day. This action cannot be undone and will also remove any associated appointments or tasks.
                      </>
                    ) : (
                      'Are you sure you want to delete this schedule item? This action cannot be undone and will also remove any associated appointments or tasks.'
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  {isMultiDayLeave && onDeleteSeries ? (
                    <>
                      <AlertDialogAction onClick={handleDelete} className="bg-secondary text-secondary-foreground hover:bg-secondary/80">
                        Delete this day only
                      </AlertDialogAction>
                      <AlertDialogAction onClick={handleDeleteEntireLeave} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        Delete entire leave ({leaveSeries?.dayCount} days)
                      </AlertDialogAction>
                    </>
                  ) : (
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Delete
                    </AlertDialogAction>
                  )}
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          
          {/* Show Check Conflicts button for leave/MC types with staff selected */}
          {(type === 'leave' || type === 'mc') && selectedStaff && !initialData && (
            <Button 
              type="button" 
              variant="secondary"
              onClick={handleCheckConflicts} 
              disabled={isSaving}
            >
              <AlertTriangle className="mr-2 h-4 w-4" />
              Check Conflicts
            </Button>
          )}
          
          <Button 
            type="button" 
            onClick={(type === 'leave' || type === 'mc') && selectedStaff && !initialData 
              ? handleCheckConflicts 
              : handleSave} 
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {initialData ? 'Update Schedule' : 'Save Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
      </Dialog>

      {/* Conflict Resolution Modal */}
      {showConflictModal && selectedStaff && (
        <ConflictResolutionModal
        isOpen={showConflictModal}
        onClose={() => setShowConflictModal(false)}
        staffId={selectedStaff}
        staffName={staff.find(s => s.id === selectedStaff)?.name || ''}
        leaveType={type as 'leave' | 'mc'}
        startDate={date}
        endDate={endDate}
        affectedSchedules={affectedSchedules}
        scheduleItems={scheduleItems}
        onConfirmReassignments={handleConfirmReassignments}
        doctors={doctors}
        staff={staff}
        rooms={rooms}
        />
      )}
    </>
  );
};

export default ScheduleModal;
